<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import type { GameMode } from '../../shared/modes';
  import type { StateMessage } from '../../shared/protocol';
  import { archiveQueryOptions } from '../queries/results';
  import type { SessionRoundResults as RoundResultsData } from '../session/room-session-state';
  import { postMatchActions } from '../app/post-match-actions';
  import PostMatch from '../results/PostMatch.svelte';
  import type { PostMatchPresentation } from '../app/post-match-presentation.svelte';

  /**
   * Live post-match entry: adapts room context to the shared presentation.
   * Action visibility is a UX reflection; the server authorizes every command.
   */
  interface Props {
    results: RoundResultsData;
    mode: GameMode;
    /** The room's result lifecycle: continuation rights and, once archived, the archived round. */
    lifecycle: Readonly<StateMessage['resultLifecycle']>;
    /** Reads the archived Daily's leaderboard place for this profile. */
    credential: string | null;
    profileGeneration: number;
    asyncRoom: boolean;
    phase: 'results' | 'playing' | 'lobby' | 'countdown';
    meId: string;
    isHost: boolean;
    hostName: string;
    /** The recap playback, its Live gate and the viewer's skip. */
    presentation: Pick<PostMatchPresentation, 'playback' | 'gated' | 'skip'>;
    playAgain: () => void;
    backToLobby: () => void;
    openLeaderboard: () => void;
  }
  let {
    results,
    mode,
    lifecycle,
    credential,
    profileGeneration,
    asyncRoom,
    phase,
    meId,
    isHost,
    hostName,
    presentation,
    playAgain,
    backToLobby,
    openLeaderboard,
  }: Props = $props();

  // A fresh Daily's leaderboard place exists once its round is archived. The detail read is
  // best-effort: without it the row simply shows no place. It also primes the archive route's cache.
  const archived = createQuery(() =>
    archiveQueryOptions(
      results.roundId,
      credential,
      profileGeneration,
      mode === 'daily' && lifecycle.archiveRoundId === results.roundId,
    ),
  );

  const actions = $derived(
    postMatchActions(
      { mode, continuation: lifecycle.continuation, asyncRoom, phase, isHost, gated: presentation.gated },
      hostName,
    ),
  );
</script>

<PostMatch
  {results}
  {mode}
  {meId}
  playback={presentation.playback}
  onSkip={() => presentation.skip()}
  dailyStanding={archived.data?.dailyStanding ?? null}
  status={actions.status}
  primaryLabel={actions.primaryLabel}
  onPrimary={actions.primaryLabel ? playAgain : null}
  lobbyLabel={actions.lobbyLabel}
  onLobby={actions.lobbyLabel ? backToLobby : null}
  leaderboardLabel={actions.leaderboardLabel}
  onLeaderboard={actions.leaderboardLabel ? openLeaderboard : null}
/>
